import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import {
  DollarSign,
  TrendingUp,
  Package,
  Percent,
  Calendar,
  Loader2
} from 'lucide-react';

const personnelAverageMetricName = 'Personel Puan Ortalaması';

const normalizeTurkishText = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim();

const adminKpiForecastTargets = [
  { key: 'company-firma', type: 'company', label: 'Firma', keyword: 'firma' },
  { key: 'person-bilal', type: 'personnel', label: 'Bilal', keyword: 'bilal' },
  { key: 'person-mete', type: 'personnel', label: 'Mete', keyword: 'mete' },
  { key: 'person-medeni', type: 'personnel', label: 'Medeni', keyword: 'medeni' },
  { key: 'person-musa', type: 'personnel', label: 'Musa', keyword: 'musa' }
];

const evaluateFormulaWithValues = (formula, valuesByMetricName) => {
  if (!formula || typeof formula !== 'string') return null;
  const replacedExpression = formula.replace(/\[([^\]]+)\]/g, (_, metricName) => {
    const parsedValue = Number(valuesByMetricName[metricName]);
    return Number.isFinite(parsedValue) ? String(parsedValue) : 'NaN';
  });
  if (/[^0-9+\-*/().\s]/.test(replacedExpression)) {
    return null;
  }
  if (replacedExpression.includes('NaN')) {
    return null;
  }
  try {
    const normalizedExpression = replacedExpression.replace(/\s+/g, '');
    const rawTokens = normalizedExpression.match(/(\d+\.\d+|\d+|[()+\-*/])/g);
    if (!rawTokens || rawTokens.join('') !== normalizedExpression) {
      return null;
    }

    const tokens = [];
    rawTokens.forEach((token, index) => {
      const previousToken = tokens[tokens.length - 1];
      const isUnaryMinus =
        token === '-' &&
        (index === 0 || previousToken === '(' || ['+', '-', '*', '/'].includes(previousToken));
      if (isUnaryMinus) {
        tokens.push('0');
        tokens.push('-');
      } else {
        tokens.push(token);
      }
    });

    const precedence = { '+': 1, '-': 1, '*': 2, '/': 2 };
    const output = [];
    const operatorStack = [];

    tokens.forEach((token) => {
      if (/^\d+(\.\d+)?$/.test(token)) {
        output.push(token);
        return;
      }
      if (token in precedence) {
        while (
          operatorStack.length > 0 &&
          operatorStack[operatorStack.length - 1] in precedence &&
          precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]
        ) {
          output.push(operatorStack.pop());
        }
        operatorStack.push(token);
        return;
      }
      if (token === '(') {
        operatorStack.push(token);
        return;
      }
      if (token === ')') {
        while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== '(') {
          output.push(operatorStack.pop());
        }
        if (operatorStack.length === 0) {
          throw new Error('invalidParenthesis');
        }
        operatorStack.pop();
      }
    });

    while (operatorStack.length > 0) {
      const operator = operatorStack.pop();
      if (operator === '(' || operator === ')') {
        throw new Error('invalidParenthesis');
      }
      output.push(operator);
    }

    const evalStack = [];
    output.forEach((token) => {
      if (/^\d+(\.\d+)?$/.test(token)) {
        evalStack.push(Number(token));
        return;
      }
      const right = evalStack.pop();
      const left = evalStack.pop();
      if (!Number.isFinite(left) || !Number.isFinite(right)) {
        throw new Error('invalidExpression');
      }
      if (token === '+') evalStack.push(left + right);
      if (token === '-') evalStack.push(left - right);
      if (token === '*') evalStack.push(left * right);
      if (token === '/') evalStack.push(right === 0 ? NaN : left / right);
    });

    if (evalStack.length !== 1) {
      return null;
    }
    const numericResult = Number(evalStack[0]);
    return Number.isFinite(numericResult) ? numericResult : null;
  } catch {
    return null;
  }
};

const resolveScoreDebugFromTable = (value, table, isPercentMetric = false) => {
  if (value === null || value === undefined) {
    return { score: null, rawValue: value, comparableValue: null };
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return { score: null, rawValue: value, comparableValue: null };
  }
  const comparableValue = isPercentMetric ? (numericValue * 100) : numericValue;
  const fullScore = Number(table?.fullScore || 0);
  const rules = Array.isArray(table?.rules) ? [...table.rules] : [];
  const sortedRules = rules
    .sort((left, right) => Number(left.orderNo || 0) - Number(right.orderNo || 0))
    .map((rule, index) => ({
      orderNo: Number(rule.orderNo || index + 1),
      minValue: Number(rule.minValue || 0),
      maxValue: Number(rule.maxValue || 0),
      scorePercent: Number(rule.scorePercent || 0),
      bonusType: rule.bonusType === 'STEP' ? 'STEP' : 'NONE',
      bonusStep: Number(rule.bonusStep || 0),
      bonusPerStep: Number(rule.bonusPerStep || 0)
    }));

  const matchedRule = sortedRules.find(
    (rule) => comparableValue >= rule.minValue && comparableValue < rule.maxValue
  );
  if (matchedRule) {
    const baseScore = fullScore * (matchedRule.scorePercent / 100);
    return {
      score: Number.isFinite(baseScore) ? baseScore : null,
      rawValue: numericValue,
      comparableValue
    };
  }

  if (table?.allowOverMax !== false) {
    const stepRule = [...sortedRules]
      .filter((rule) => rule.bonusType === 'STEP')
      .sort((left, right) => right.maxValue - left.maxValue)
      .find((rule) => comparableValue >= rule.maxValue);
    if (stepRule) {
      const topRule = [...sortedRules].sort((left, right) => right.maxValue - left.maxValue)[0] || null;
      const topRuleMaxValue = Number(topRule?.maxValue || stepRule.maxValue || 0);
      const overValue = comparableValue - topRuleMaxValue;
      const bonusCount = stepRule.bonusStep > 0 ? (overValue > 0 ? Math.ceil(overValue / stepRule.bonusStep) : 0) : 0;
      return {
        score: fullScore + (bonusCount * stepRule.bonusPerStep),
        rawValue: numericValue,
        comparableValue
      };
    }
  }

  return {
    score: null,
    rawValue: numericValue,
    comparableValue
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpiForecastCards, setKpiForecastCards] = useState([]);
  const [kpiForecastLoading, setKpiForecastLoading] = useState(true);
  const [kpiForecastError, setKpiForecastError] = useState('');
  const [openKpiDetailKey, setOpenKpiDetailKey] = useState('');

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/dashboard/stats');

      if (response.data.success) {
        setStats(response.data.data);
      } else {
        throw new Error(response.data.message || 'Veriler alınamadı');
      }
    } catch (requestError) {
      console.error('❌ Dashboard stats error:', requestError);

      let errorMessage = 'Dashboard verileri alınamadı';

      if (requestError.response?.status === 401) {
        errorMessage = 'Oturum süresi dolmuş. Lütfen tekrar giriş yapın.';
      } else if (requestError.response?.data?.message) {
        errorMessage = requestError.response.data.message;
      } else if (requestError.message) {
        errorMessage = requestError.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchKpiForecasts = useCallback(async () => {
    try {
      setKpiForecastLoading(true);
      setKpiForecastError('');

      const now = new Date();
      const currentPeriod = {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        key: `${now.getFullYear()}-${now.getMonth() + 1}`,
        isCurrent: true
      };

      const [assigneesResponse, selectedResponse, metricsResponse] = await Promise.all([
        api.get('/kpi/assignees'),
        api.get('/kpi/selected-personnel'),
        api.get('/kpi/metrics')
      ]);

      const personnelList = Array.isArray(assigneesResponse?.data?.data?.personnel)
        ? assigneesResponse.data.data.personnel
        : [];
      const companyList = Array.isArray(assigneesResponse?.data?.data?.companies)
        ? assigneesResponse.data.data.companies
        : [];
      const selectedPersonnelIds = Array.isArray(selectedResponse?.data?.data)
        ? selectedResponse.data.data.map((id) => Number(id)).filter((id) => id > 0)
        : [];
      const metricDefs = Array.isArray(metricsResponse?.data?.data)
        ? metricsResponse.data.data
        : [];

      const findByKeyword = (items, keyword) => {
        const normalizedKeyword = normalizeTurkishText(keyword);
        return items.find((item) => normalizeTurkishText(item?.name).includes(normalizedKeyword)) || null;
      };

      const isAdminUser = String(user?.role || '').toLowerCase() === 'admin';
      const targetAssignees = isAdminUser
        ? adminKpiForecastTargets.map((target) => {
            if (target.type === 'personnel') {
              return { ...target, assignee: findByKeyword(personnelList, target.keyword) };
            }
            return { ...target, assignee: findByKeyword(companyList, target.keyword) || companyList[0] || null };
          })
        : [
            ...companyList.slice(0, 1).map((company) => ({
              key: `company-${company.id}`,
              type: 'company',
              label: 'Şirket',
              assignee: company
            })),
            ...personnelList.slice(0, 5).map((personnel) => ({
              key: `personnel-${personnel.id}`,
              type: 'personnel',
              label: personnel.name || 'Personel',
              assignee: personnel
            }))
          ];

      const targetPersonnelIds = targetAssignees
        .filter((target) => target.type === 'personnel' && target.assignee?.id)
        .map((target) => Number(target.assignee.id))
        .filter((id) => id > 0);
      const allPersonnelIds = [...new Set([...selectedPersonnelIds, ...targetPersonnelIds])];

      const historicalResponse =
        allPersonnelIds.length > 0
          ? await api.get('/kpi/historical-test-values', {
              params: {
                periods: JSON.stringify([currentPeriod]),
                personnelIds: allPersonnelIds.join(','),
                preferExistingOnly: 'true',
                backgroundRefresh: 'true'
              },
              timeout: 120000
            })
          : { data: { success: true, data: { snapshots: {} } } };

      const currentSnapshot = historicalResponse?.data?.data?.snapshots?.[currentPeriod.key] || {};
      const baseValuesByMetricName = {};
      Object.keys(currentSnapshot).forEach((metricName) => {
        const numericValue = Number(currentSnapshot[metricName]);
        baseValuesByMetricName[metricName] = Number.isFinite(numericValue) ? numericValue : currentSnapshot[metricName];
      });

      const leaveDaysByPersonnelId = {};
      personnelList.forEach((personnel) => {
        const leaveValue = Number(currentSnapshot[`İzin Gün Sayısı - ${personnel.name}`] || 0);
        leaveDaysByPersonnelId[Number(personnel.id)] = Number.isFinite(leaveValue) ? leaveValue : 0;
      });

      const metrics = metricDefs.map((metric) => ({
        id: `db-${metric.id}`,
        name: String(metric.name || ''),
        formula: String(metric.formula || ''),
        isPercent: Boolean(metric.isPercent),
        tables: Array.isArray(metric.tables) ? metric.tables : []
      }));

      const customValueByMetricName = {};
      metrics.forEach((metric) => {
        customValueByMetricName[metric.name] = evaluateFormulaWithValues(metric.formula, baseValuesByMetricName);
      });

      const getPersonnelMetricValueForPeriod = (metric, personnelId) => {
        if (!metric) return null;
        if (metric.formula.includes('[İzin Gün Sayısı]')) {
          const formulaValues = { ...baseValuesByMetricName, 'İzin Gün Sayısı': leaveDaysByPersonnelId[Number(personnelId)] || 0 };
          return evaluateFormulaWithValues(metric.formula, formulaValues);
        }
        const value = customValueByMetricName[metric.name];
        return Number.isFinite(Number(value)) ? Number(value) : null;
      };

      const sumScoreRows = (rows) => {
        let total = 0;
        let count = 0;
        rows.forEach((row) => {
          if (Number.isFinite(Number(row?.score))) {
            total += Number(row.score);
            count += 1;
          }
        });
        return count > 0 ? total : null;
      };

      const getPersonnelScoreRows = (personnelId) =>
        metrics
          .map((metric) => {
            const assignedTable = (metric.tables || []).find((table) =>
              (table.assignments || []).some(
                (assignment) =>
                  assignment.assigneeType === 'personnel' && Number(assignment.assigneeId) === Number(personnelId)
              )
            );
            if (!assignedTable) return null;
            const metricValue = getPersonnelMetricValueForPeriod(metric, personnelId);
            const debug = resolveScoreDebugFromTable(metricValue, assignedTable, Boolean(metric.isPercent));
            return {
              metricName: metric.name,
              fullScore: Number(assignedTable.fullScore || 0),
              score: Number.isFinite(Number(debug.score)) ? Number(debug.score) : null,
              rawValue: debug.rawValue,
              isPercentMetric: Boolean(metric.isPercent)
            };
          })
          .filter(Boolean);

      const personnelAverageScore = (() => {
        let total = 0;
        let count = 0;
        selectedPersonnelIds.forEach((personnelId) => {
          const rows = getPersonnelScoreRows(personnelId).filter(
            (row) => String(row.metricName || '').trim() !== personnelAverageMetricName
          );
          const rowTotal = sumScoreRows(rows);
          if (Number.isFinite(Number(rowTotal))) {
            total += Number(rowTotal);
            count += 1;
          }
        });
        return count > 0 ? (total / count) : null;
      })();

      const getCompanyScoreRows = (companyId) =>
        metrics
          .map((metric) => {
            const assignedTable = (metric.tables || []).find((table) =>
              (table.assignments || []).some(
                (assignment) =>
                  assignment.assigneeType === 'company' && Number(assignment.assigneeId) === Number(companyId)
              )
            );
            if (!assignedTable) return null;
            const metricValue =
              String(metric.name || '').trim() === personnelAverageMetricName
                ? personnelAverageScore
                : customValueByMetricName[metric.name];
            const debug = resolveScoreDebugFromTable(metricValue, assignedTable, Boolean(metric.isPercent));
            return {
              metricName: metric.name,
              fullScore: Number(assignedTable.fullScore || 0),
              score: Number.isFinite(Number(debug.score)) ? Number(debug.score) : null,
              rawValue: debug.rawValue,
              isPercentMetric: Boolean(metric.isPercent)
            };
          })
          .filter(Boolean);

      const nextCards = targetAssignees.map((target) => {
        if (!target.assignee?.id) {
          return {
            key: target.key,
            label: target.label,
            assigneeName: '-',
            score: null,
            rows: []
          };
        }
        const rows =
          target.type === 'company'
            ? getCompanyScoreRows(target.assignee.id)
            : getPersonnelScoreRows(target.assignee.id);
        return {
          key: target.key,
          label: target.label,
          assigneeName: target.assignee.name || target.label,
          score: sumScoreRows(rows),
          rows
        };
      });

      setKpiForecastCards(nextCards);
    } catch (requestError) {
      const statusCode = Number(requestError?.response?.status || 0);
      if (statusCode === 401 || statusCode === 403) {
        setKpiForecastCards([]);
        setKpiForecastError('KPI görüntüleme yetkiniz yok');
        return;
      }
      console.error('❌ KPI forecast error:', requestError);
      setKpiForecastError('KPI tahminleri alınamadı');
      setKpiForecastCards([]);
    } finally {
      setKpiForecastLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchDashboardStats();
    fetchKpiForecasts();
  }, [fetchDashboardStats, fetchKpiForecasts]);

  const handleRefresh = useCallback(() => {
    fetchDashboardStats();
    fetchKpiForecasts();
  }, [fetchDashboardStats, fetchKpiForecasts]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }, []);

  const formatProjectedRevenue = useCallback((amount) => {
    const numValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d.-]/g, '')) : (amount || 0);
    const rounded = Math.floor(Number(numValue) / 1000) * 1000;
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(rounded);
  }, []);

  const formatNumber = useCallback((number) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(number);
  }, []);

  const formatTonnage = useCallback((units) => {
    if (!units || Object.keys(units).length === 0) {
      return 'Veri yok';
    }

    return Object.entries(units)
      .filter(([_, value]) => value > 0)
      .map(([unit, value]) => `${formatNumber(value)} ${unit}`)
      .join(' / ');
  }, [formatNumber]);

  const formatKpiValue = useCallback((value, isPercent = false) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '-';
    const formatted = new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numericValue);
    return isPercent ? `%${formatted}` : formatted;
  }, []);

  const renderKpiScoreBadge = useCallback((scoreValue) => {
    const numericScore = Number(scoreValue);
    const safeScore = Number.isFinite(numericScore) ? numericScore : 0;
    const progressPercent = Math.max(0, Math.min(100, safeScore));
    const size = 64;
    const stroke = 6;
    const center = size / 2;
    const radius = center - stroke;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - progressPercent / 100);
    const isBelowThreshold = safeScore < 70;
    const progressColorClass = isBelowThreshold ? 'stroke-red-500' : 'stroke-green-500';

    return (
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90">
          <circle
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
            className="stroke-gray-200 dark:stroke-gray-700 fill-transparent"
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className={`${progressColorClass} fill-transparent transition-all duration-300`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-base font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
          {Math.round(safeScore)}
        </div>
      </div>
    );
  }, []);

  const StatCard = useMemo(() => ({ title, value, subtitle, icon: Icon, color = 'blue', onClick }) => {
    const colorClasses = {
      blue: {
        bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
        border: isDark ? 'border-blue-800' : 'border-blue-200',
        icon: isDark ? 'text-blue-400' : 'text-blue-600',
        text: isDark ? 'text-blue-100' : 'text-blue-900'
      },
      green: {
        bg: isDark ? 'bg-green-900/20' : 'bg-green-50',
        border: isDark ? 'border-green-800' : 'border-green-200',
        icon: isDark ? 'text-green-400' : 'text-green-600',
        text: isDark ? 'text-green-100' : 'text-green-900'
      },
      purple: {
        bg: isDark ? 'bg-purple-900/20' : 'bg-purple-50',
        border: isDark ? 'border-purple-800' : 'border-purple-200',
        icon: isDark ? 'text-purple-400' : 'text-purple-600',
        text: isDark ? 'text-purple-100' : 'text-purple-900'
      },
      orange: {
        bg: isDark ? 'bg-orange-900/20' : 'bg-orange-50',
        border: isDark ? 'border-orange-800' : 'border-orange-200',
        icon: isDark ? 'text-orange-400' : 'text-orange-600',
        text: isDark ? 'text-orange-100' : 'text-orange-900'
      }
    };

    const classes = colorClasses[color];

    return (
      <div
        className={`p-4 sm:p-6 rounded-lg border ${classes.bg} ${classes.border} transition-all duration-200 hover:shadow-lg ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {title}
            </p>
            <p className={`text-xl sm:text-2xl font-bold mt-2 ${classes.text}`}>
              {value}
            </p>
            {subtitle && (
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {subtitle}
              </p>
            )}
          </div>
          <div className={`p-2.5 sm:p-3 rounded-full ${classes.bg}`}>
            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${classes.icon}`} />
          </div>
        </div>
      </div>
    );
  }, [isDark]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 dark:text-blue-400" />
            <p className="text-gray-500 dark:text-gray-400 text-lg mt-4">Dashboard verileri yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 dark:text-red-400 text-lg">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Calendar className="h-4 w-4" />
          Yenile
        </button>
      </div>

      {stats?.targetProgress && (
        <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Hedef</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                ${formatProjectedRevenue(stats.targetProgress.targetRevenue)}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Gerçekleşen</p>
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(parseFloat(stats.targetProgress.actualRevenue))}
              </p>
            </div>
          </div>

          <div className="relative w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-3">
            <div
              className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${
                parseFloat(stats.targetProgress.achievementRate) >= 90
                  ? 'bg-green-500'
                  : parseFloat(stats.targetProgress.achievementRate) >= 70
                    ? 'bg-orange-500'
                    : 'bg-red-500'
              }`}
              style={{
                width: `${Math.min(parseFloat(stats.targetProgress.achievementRate), 100)}%`
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2 mt-2">
            <p className={`text-xs font-semibold ${
              parseFloat(stats.targetProgress.achievementRate) >= 90
                ? 'text-green-600 dark:text-green-400'
                : parseFloat(stats.targetProgress.achievementRate) >= 70
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-red-600 dark:text-red-400'
            }`}>
              %{stats.targetProgress.achievementRate}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tahmini: ${formatProjectedRevenue(stats.targetProgress.projectedRevenue)}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Günlük Satış"
          value={formatCurrency(parseFloat(stats?.dailySales?.amount || 0))}
          subtitle={`${stats?.dailySales?.date || 'Bugün'}`}
          icon={DollarSign}
          color="green"
          onClick={() => navigate('/reports/sales?tab=customer&dateRange=today')}
        />

        <StatCard
          title="Aylık Ciro"
          value={formatCurrency(parseFloat(stats?.monthlyRevenue?.amount || 0))}
          subtitle={`${stats?.monthlyRevenue?.month || 'Bu Ay'}`}
          icon={TrendingUp}
          color="blue"
        />

        <StatCard
          title="Aylık Tonaj"
          value={formatTonnage(stats?.monthlyTonnage?.units)}
          subtitle={`${stats?.monthlyTonnage?.month || 'Bu Ay'}`}
          icon={Package}
          color="purple"
        />

        <StatCard
          title="Aylık İade Oranı"
          value={`%${stats?.monthlyReturnRate?.rate || '0.00'}`}
          subtitle={`${stats?.monthlyReturnRate?.month || 'Bu Ay'}`}
          icon={Percent}
          color="orange"
        />
      </div>

      <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">KPI Tahminleri</h3>
        </div>
        {kpiForecastLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            KPI tahminleri yükleniyor...
          </div>
        ) : kpiForecastError ? (
          <p className="text-sm text-red-500 dark:text-red-400">{kpiForecastError}</p>
        ) : kpiForecastCards.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Görüntülenecek KPI kartı bulunmuyor.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            {kpiForecastCards.map((card) => {
              const scoreValue = Number(card.score);
              const validScore = Number.isFinite(scoreValue) ? scoreValue : 0;
              const hasRows = Array.isArray(card.rows) && card.rows.length > 0;
              const isOpenOnMobile = openKpiDetailKey === card.key;
              return (
                <div
                  key={card.key}
                  className={`group relative rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50'} overflow-visible`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {renderKpiScoreBadge(validScore)}
                      {hasRows && (
                        <div className={`hidden sm:block pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 z-20 w-[320px] rounded-lg border shadow-lg p-2 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                          <div className={`max-h-56 overflow-auto text-[11px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                            <table className="w-full">
                              <thead>
                                <tr className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                                  <th className="text-left py-1 pr-2">Metrik</th>
                                  <th className="text-right py-1 px-1">Tam</th>
                                  <th className="text-right py-1 px-1">Değer</th>
                                  <th className="text-right py-1 pl-1">Puan</th>
                                </tr>
                              </thead>
                              <tbody>
                                {card.rows.map((row) => (
                                  <tr key={`${card.key}-${row.metricName}`} className="border-t border-gray-200/70 dark:border-gray-700/70">
                                    <td className="py-1 pr-2">{row.metricName}</td>
                                    <td className="py-1 px-1 text-right tabular-nums">{formatKpiValue(row.fullScore)}</td>
                                    <td className="py-1 px-1 text-right tabular-nums">
                                      {formatKpiValue(row.rawValue, row.isPercentMetric)}
                                    </td>
                                    <td className="py-1 pl-1 text-right tabular-nums font-medium">{formatKpiValue(row.score)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{card.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{card.assigneeName}</p>
                      <p className="text-xs mt-1 text-gray-600 dark:text-gray-300">Toplam: {formatKpiValue(card.score)} / 100</p>
                      {hasRows && (
                        <button
                          type="button"
                          onClick={() => setOpenKpiDetailKey((prev) => (prev === card.key ? '' : card.key))}
                          className="sm:hidden mt-2 px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 text-[11px] text-gray-700 dark:text-gray-200"
                        >
                          {isOpenOnMobile ? 'Detayı Gizle' : 'Detayı Gör'}
                        </button>
                      )}
                    </div>
                  </div>
                  {hasRows && isOpenOnMobile && (
                    <div className={`sm:hidden mt-3 rounded-lg border p-2 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div className={`max-h-56 overflow-auto text-[11px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        <table className="w-full">
                          <thead>
                            <tr className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                              <th className="text-left py-1 pr-2">Metrik</th>
                              <th className="text-right py-1 px-1">Tam</th>
                              <th className="text-right py-1 px-1">Değer</th>
                              <th className="text-right py-1 pl-1">Puan</th>
                            </tr>
                          </thead>
                          <tbody>
                            {card.rows.map((row) => (
                              <tr key={`${card.key}-mobile-${row.metricName}`} className="border-t border-gray-200/70 dark:border-gray-700/70">
                                <td className="py-1 pr-2">{row.metricName}</td>
                                <td className="py-1 px-1 text-right tabular-nums">{formatKpiValue(row.fullScore)}</td>
                                <td className="py-1 px-1 text-right tabular-nums">
                                  {formatKpiValue(row.rawValue, row.isPercentMetric)}
                                </td>
                                <td className="py-1 pl-1 text-right tabular-nums font-medium">{formatKpiValue(row.score)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {stats?.exchangeRates && (
        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Güncel Döviz Kurları
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">USD/TRY:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.USDTRY?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">EUR/TRY:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.EURTRY?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">EUR/USD:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.EURUSD?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
